import { put, call, takeLatest, takeEvery, cancelled } from 'redux-saga/effects';
import {
  CASE_FETCH_FILE_SEARCH_HISTORY_BEGIN,
  CASE_FETCH_FILE_SEARCH_HISTORY_SUCCESS,
  CASE_FETCH_FILE_SEARCH_HISTORY_FAILURE,
  CASE_FETCH_FILE_SEARCH_HISTORY_DISMISS_FEEDBACK,
  CASE_CANCEL_FILE_SEARCH_HISTORY,
  CASE_UPDATE_SEARCH_HISTORY_ITEM_SUCCESS,
} from './constants';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';

export const fetchFileSearchHistory = createPromiseAction(CASE_FETCH_FILE_SEARCH_HISTORY_BEGIN);

// Simple action creator for cancellation (not promise-based)
export const cancelFetchFileSearchHistory = () => ({
  type: CASE_CANCEL_FILE_SEARCH_HISTORY,
});

// Store for tracking cancellation state and AbortController
let isCancellationRequested = false;
let currentAbortController = null;

export function dismissFetchFileSearchHistoryFeedback() {
  return {
    type: CASE_FETCH_FILE_SEARCH_HISTORY_DISMISS_FEEDBACK,
  };
}

// worker Saga: will be fired on CASE_FETCH_FILE_SEARCH_HISTORY_BEGIN actions
export function* doFetchFileSearchHistory(action) {
  const {
    payload: { caseId, fileId },
  } = action;

  // Cancel any existing request
  if (currentAbortController) {
    currentAbortController.abort();
  }

  // Reset cancellation flag and create new AbortController
  isCancellationRequested = false;
  currentAbortController = new AbortController();

  try {
    // Build URL with fileId parameter
    const url = `/cases/${caseId}/search-history?fileId=${fileId}`;

    // Make the API call with AbortController
    const res = yield call(api.get, url, { signal: currentAbortController.signal });

    // Check if cancellation was requested while the call was in progress
    if (isCancellationRequested) {
      // Request was cancelled after it completed - resolve as cancelled
      currentAbortController = null;
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Clear the controller on successful completion
    currentAbortController = null;

    if (res && res.error) {
      yield put({
        type: CASE_FETCH_FILE_SEARCH_HISTORY_FAILURE,
        feedback: {
          message: 'feedback.fetchFileSearchHistoryFailure',
          error: res.error,
        },
      });
      return yield call(rejectPromiseAction, action, res.error);
    }
    
    // Dispatch success action
    yield put({
      type: CASE_FETCH_FILE_SEARCH_HISTORY_SUCCESS,
      data: {
        results: res,
      },
    });
    yield call(resolvePromiseAction, action, res);
  } catch (error) {
    // Clear the controller on error
    currentAbortController = null;

    // Check if it was an abort error (cancellation)
    if (error.name === 'AbortError' || 
        (error.message && (error.message.includes('aborted') || error.message.includes('signal is aborted'))) ||
        isCancellationRequested) {
      // Request was cancelled - resolve as cancelled to prevent promise rejection
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Handle other errors
    yield put({
      type: CASE_FETCH_FILE_SEARCH_HISTORY_FAILURE,
      feedback: {
        message: 'feedback.fetchFileSearchHistoryFailure',
        error: error.message || error,
      },
    });
    yield call(rejectPromiseAction, action, error);
  } finally {
    // Handle saga cancellation
    if (yield cancelled()) {
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      isCancellationRequested = true;
    }
  }
}

// worker Saga: will be fired on CASE_CANCEL_FILE_SEARCH_HISTORY actions
export function* doCancelFileSearchHistory() {
  // Set the cancellation flag
  isCancellationRequested = true;
  
  // Abort the current HTTP request
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

export function* watchFetchFileSearchHistory() {
  yield takeLatest(fetchFileSearchHistory, withCurrentCaseId(doFetchFileSearchHistory));
}

export function* watchCancelFileSearchHistory() {
  yield takeEvery(CASE_CANCEL_FILE_SEARCH_HISTORY, doCancelFileSearchHistory);
}

export function useFetchFileSearchHistory() {
  const dispatch = useDispatch();

  const { fileSearchHistory, fetchFileSearchHistoryPending, fetchFileSearchHistoryFeedback } =
    useSelector(
      (state) => ({
        fileSearchHistory: state.case.fileSearchHistory,
        fetchFileSearchHistoryPending: state.case.fetchFileSearchHistoryPending,
        fetchFileSearchHistoryFeedback: state.case.fetchFileSearchHistoryFeedback,
      }),
      shallowEqual,
    );

  const boundAction = useCallback(
    (fileId) => {
      return dispatch(fetchFileSearchHistory({ fileId }));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissFetchFileSearchHistoryFeedback());
  }, [dispatch]);

  const boundCancelAction = useCallback(() => {
    return dispatch(cancelFetchFileSearchHistory());
  }, [dispatch]);

  return {
    fileSearchHistory,
    fetchFileSearchHistory: boundAction,
    fetchFileSearchHistoryPending,
    fetchFileSearchHistoryFeedback,
    dismissFetchFileSearchHistoryFeedback: boundDismissFeedback,
    cancelFetchFileSearchHistory: boundCancelAction,
  };
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case CASE_FETCH_FILE_SEARCH_HISTORY_BEGIN + '.TRIGGER':
      return {
        ...state,
        fetchFileSearchHistoryPending: true,
        fetchFileSearchHistoryFeedback: action.feedback,
        fileSearchHistory: [],
      };

    case CASE_FETCH_FILE_SEARCH_HISTORY_SUCCESS:
      return {
        ...state,
        fetchFileSearchHistoryPending: false,
        fetchFileSearchHistoryFeedback: action.feedback,
        fileSearchHistory:
          action?.data && action.data.results && action.data.results.length > 0 ? action.data.results : [],
      };

    case CASE_FETCH_FILE_SEARCH_HISTORY_FAILURE:
      return {
        ...state,
        fetchFileSearchHistoryPending: false,
        fileSearchHistory: [],
        fetchFileSearchHistoryFeedback: action.feedback,
      };

    case CASE_FETCH_FILE_SEARCH_HISTORY_DISMISS_FEEDBACK:
      return {
        ...state,
        fetchFileSearchHistoryFeedback: null,
      };

    case CASE_CANCEL_FILE_SEARCH_HISTORY:
      return {
        ...state,
        fetchFileSearchHistoryPending: false,
        fetchFileSearchHistoryFeedback: null,
      };

    case CASE_UPDATE_SEARCH_HISTORY_ITEM_SUCCESS:
      // Update the fileSearchHistory when a search history item is updated
      if (Array.isArray(state.fileSearchHistory) && action.data.searchHistoryItem) {
        const updatedHistory = state.fileSearchHistory.map((item) =>
          item.id === action.data.searchHistoryItem.id ? action.data.searchHistoryItem : item,
        );
        return {
          ...state,
          fileSearchHistory: updatedHistory,
        };
      } else {
          return {
          ...state,
          fileSearchHistory: [],
        };
      }
      return state;

    default:
      return state;
  }
}
